import React, { useEffect, useRef, useState } from 'react';
import { Bar } from '@nivo/bar';
import tinycolor from 'tinycolor2';
import { splitCandidateLabelLines } from './rankChartPrintLabels';
import { formatPrintChartContribution, getPrintChartDisplayRows, getPrintChartTotal } from './rankChartPrintData';
import {
  getRankChartPrintBarLabelFontSize,
  getRankChartPrintCandidateLabelMaxLines,
  getRankChartPrintGraphHeight,
  getRankChartPrintLayout,
} from './rankChartPrintLayout';

const CANDIDATE_LABEL_FONT = '11px sans-serif';

const measureLabelText = (value) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = CANDIDATE_LABEL_FONT;
  return context.measureText(value).width;
};

const RankChartPrintGraph = ({ keys = [], layoutId, paperId, rows = [] }) => {
  const printLayout = getRankChartPrintLayout(layoutId, paperId);
  const graphContainerRef = useRef(null);
  const [graphWidth, setGraphWidth] = useState(printLayout.graphWidth);
  const keyLabels = keys.map((key) => key.label);
  const colors = keys.map((key) => key.color);
  const displayRows = getPrintChartDisplayRows(rows);
  const graphHeight = getRankChartPrintGraphHeight(layoutId, keys.length, paperId);
  const barLabelFontSize = getRankChartPrintBarLabelFontSize(layoutId, keys.length);
  const candidateLabelMaxLines = getRankChartPrintCandidateLabelMaxLines(layoutId, rows.length);

  useEffect(() => {
    const graphContainer = graphContainerRef.current;
    if (!graphContainer) {
      return undefined;
    }

    const updateGraphWidth = () => {
      const nextWidth = Math.floor(graphContainer.getBoundingClientRect().width);
      if (nextWidth > 0) {
        setGraphWidth(nextWidth);
      }
    };

    updateGraphWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateGraphWidth);
      return () => window.removeEventListener('resize', updateGraphWidth);
    }

    const resizeObserver = new ResizeObserver(updateGraphWidth);
    resizeObserver.observe(graphContainer);
    return () => resizeObserver.disconnect();
  }, [printLayout.graphWidth]);

  return (
    <div className="rank-chart-print-graph" ref={graphContainerRef} style={{ height: graphHeight }}>
      <Bar
        width={graphWidth}
        height={parseFloat(graphHeight) * 96}
        data={displayRows}
        keys={keyLabels}
        indexBy="displayName"
        margin={printLayout.graphMargin}
        maxValue={100}
        minValue={0}
        enableGridX={false}
        enableGridY={true}
        padding={0.2}
        layout="horizontal"
        theme={{ labels: { text: { fontSize: barLabelFontSize } } }}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        valueFormat={formatPrintChartContribution}
        colors={colors}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: '',
          renderTick: (tick) => {
            const row = displayRows[tick.tickIndex];
            if (!row) {
              return null;
            }
            const total = getPrintChartTotal(row, keys);
            return (
              <g transform={`translate(${tick.x + 30},${tick.y})`}>
                <rect x={-14} y={-8} rx={3} ry={3} width={54} height={24} fill="rgba(0, 0, 0, .05)" />
                <rect x={-12} y={-12} rx={2} ry={2} width={54} height={24} fill="rgb(232, 193, 160)" />
                <line stroke="rgb(232, 193, 160)" strokeWidth={1.5} x1={-35} x2={-12} />
                <text y={5} style={{ fill: '#333', fontSize: 12, fontWeight: 'bold' }}>
                  {total.toFixed(2)}
                </text>
              </g>
            );
          },
        }}
        axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: '' }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: '',
          renderTick: (tick) => {
            const labelLines = splitCandidateLabelLines(
              tick.value,
              printLayout.candidateLabelWidth,
              measureLabelText,
              candidateLabelMaxLines,
            );

            return (
              <g transform={`translate(${tick.x},${tick.y})`}>
                <text x={-10} textAnchor="end" style={{ fontSize: 11 }}>
                  {labelLines.map((labelLine, index) => (
                    <tspan key={`${labelLine}-${index}`} x={-10} dy={index === 0 ? 4 - (labelLines.length - 1) * 6 : 12}>
                      {labelLine}
                    </tspan>
                  ))}
                </text>
              </g>
            );
          },
        }}
        labelSkipWidth={10}
        labelSkipHeight={12}
        labelTextColor={(bar) => {
          if (tinycolor(bar.color).isLight()) {
            return { from: 'color', modifiers: [['darker', 1.6]] };
          }
          return 'white';
        }}
        animate={false}
        isInteractive={false}
      />
    </div>
  );
};

export default RankChartPrintGraph;
